// Header.js (FULLY UPDATED with working search: Products + Pages + Custom Fabric ID)
// ✅ Search is a Cart-style Side Panel (no buggy dropdown / outside-click issues)

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/apiConfig';
import { SEARCH_PAGES } from '../config/searchPages';

const SESSION_CART_KEY = 'ccc_guest_cart';
const SESSION_WISHLIST_KEY = 'ccc_guest_wishlist';

const safeParse = (raw, fallback) => {
  try {
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const getGuestCart = () => safeParse(sessionStorage.getItem(SESSION_CART_KEY), []);
const setGuestCart = (items) => {
  try {
    sessionStorage.setItem(SESSION_CART_KEY, JSON.stringify(items || []));
  } catch {}
  window.dispatchEvent(new Event('ccc_cart_updated'));
};

const deleteGuestCartItem = (guestCartId) => {
  const items = getGuestCart().filter((x) => x.guestCartId !== guestCartId);
  setGuestCart(items);
};

// ---- WISHLIST (guest) ----
// (You said you don't want wishlist count in header; keeping helpers for consistency)
const getGuestWishlist = () => safeParse(sessionStorage.getItem(SESSION_WISHLIST_KEY), []);
const setGuestWishlist = (items) => {
  try {
    sessionStorage.setItem(SESSION_WISHLIST_KEY, JSON.stringify(items || []));
  } catch {}
  window.dispatchEvent(new Event('ccc_wishlist_updated'));
};

const deleteGuestWishlistItem = (guestWishlistId) => {
  const items = getGuestWishlist().filter((x) => x.guestWishlistId !== guestWishlistId);
  setGuestWishlist(items);
};

const normalize = (s) => (s || '').toLowerCase().trim();

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [headerData, setHeaderData] = useState(null);
  const [showCart, setShowCart] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openMobileMenus, setOpenMobileMenus] = useState([]);

  // ✅ Search as Cart-style side panel
  const [showSearchPanel, setShowSearchPanel] = useState(false);

  // ✅ Search UI state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [productSuggestions, setProductSuggestions] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);

  const searchInputRef = useRef(null);

  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem('user');
    try {
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const [cartItems, setCartItems] = useState(() => getGuestCart());

  const toggleMobileMenu = (index) => {
    setOpenMobileMenus((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  // ✅ Pages suggestions (client-side)
  const pageSuggestions = useMemo(() => {
    const term = normalize(searchQuery);
    if (!term) return [];
    return SEARCH_PAGES.filter((p) => normalize(p.title).includes(term))
      .slice(0, 6)
      .map((p) => ({
        type: 'Page',
        title: p.title,
        subtitle: p.path,
        onSelect: () => {
          handleCloseSearch();
          navigate(p.path);
        }
      }));
  }, [searchQuery, navigate]);

  // ✅ Unified list: API results + Pages (single list)
  const combinedSuggestions = useMemo(() => {
    const items = [];

    // API results from /api/products/search
    // Expected shape (your current backend):
    // [{ id, title, imageUrl, defaultTotalPrice, matchedCustomFabricId }]
    (productSuggestions || []).forEach((x) => {
      const id = x.id ?? x.productId;
      const title = x.title ?? x.productName ?? 'Product';

      items.push({
        type: x.matchedCustomFabricId ? 'Custom Fabric ID' : 'Product',
        title,
        subtitle: x.matchedCustomFabricId
          ? `Custom Fabric ID: ${x.matchedCustomFabricId}`
          : id
          ? `Product ID: ${id}`
          : '',
        onSelect: () => {
          if (!id) return;
          handleCloseSearch();
          navigate(`/product/${id}`);
        }
      });
    });

    // pages at end
    items.push(...pageSuggestions);

    return items.slice(0, 10);
  }, [productSuggestions, pageSuggestions, navigate]);

  const handleOpenSearch = () => {
    document.body.classList.remove('cart-sidenav-menu-active');
    setShowSearchPanel(true);
    setSearchError('');
    setActiveIndex(-1);
    setTimeout(() => searchInputRef.current?.focus(), 0);
  };

  const handleCloseSearch = () => {
    setShowSearchPanel(false);
    setActiveIndex(-1);
    document.body.classList.remove('cart-sidenav-menu-active');
  };

  const runSearch = (termRaw) => {
    const term = (termRaw || '').trim();
    if (!term) return;
    handleCloseSearch();
    navigate(`/search?q=${encodeURIComponent(term)}`);
  };

  const chooseSuggestion = (item) => {
    if (!item) return;
    item.onSelect?.();
  };

  // ✅ ESC closes panels
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (showSearchPanel) handleCloseSearch();
        if (showCart) setShowCart(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showSearchPanel, showCart]);

  // Ensure any legacy template overlay classes are cleaned up on route change.
  useEffect(() => {
    document.body.classList.remove('cart-sidenav-menu-active', 'side-nav-opened');
  }, [location.pathname, location.search, location.hash]);

  // ✅ Debounced unified suggestions (server-side)
  // Hits: /api/products/search?q=
  useEffect(() => {
    let t = null;
    let alive = true;

    const term = searchQuery.trim();

    if (!term) {
      setProductSuggestions([]);
      setSearchLoading(false);
      setSearchError('');
      setActiveIndex(-1);
      return;
    }

    t = setTimeout(async () => {
      setSearchLoading(true);
      setSearchError('');
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/products/search?q=${encodeURIComponent(term)}`
        );
        if (!res.ok) throw new Error('Search failed');
        const data = await res.json();
        if (!alive) return;

        setProductSuggestions(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!alive) return;
        setProductSuggestions([]);
        setSearchError('Search unavailable');
      } finally {
        if (alive) setSearchLoading(false);
      }
    }, 250);

    return () => {
      alive = false;
      if (t) clearTimeout(t);
    };
  }, [searchQuery]);

  // Single, safe user sync (no duplicates)
  useEffect(() => {
    const syncUser = () => {
      try {
        const raw = localStorage.getItem('user');
        setUser(raw ? JSON.parse(raw) : null);
      } catch {
        setUser(null);
      }
    };

    window.addEventListener('storage', syncUser); // other tabs
    window.addEventListener('ccc_user_updated', syncUser); // same tab
    return () => {
      window.removeEventListener('storage', syncUser);
      window.removeEventListener('ccc_user_updated', syncUser);
    };
  }, []);

  // Keep cart count correct when user logs in/out
  useEffect(() => {
    if (!user) {
      setCartItems(getGuestCart());
      return;
    }

    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(
          `${API_BASE_URL}/UserManagement/GetCartByUserId/${user.userId}`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          }
        );
        const data = await res.json();
        setCartItems(Array.isArray(data) ? data : []);
      } catch {
        setCartItems([]);
      }
    })();
  }, [user]);

  // Guest cart sync when sessionStorage changes (via custom event)
  useEffect(() => {
    const syncGuestCart = () => {
      if (!user) setCartItems(getGuestCart());
    };
    window.addEventListener('ccc_cart_updated', syncGuestCart);
    return () => window.removeEventListener('ccc_cart_updated', syncGuestCart);
  }, [user]);

  useEffect(() => {
    const fetchHeader = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/header/api/data`);
        const data = await res.json();
        setHeaderData(data);
      } catch (error) {
        console.error('Failed to fetch header:', error);
      }
    };
    fetchHeader();
  }, []);

  const handleOpenCart = async () => {
    setShowCart(true);

    // Guest flow
    if (!user) {
      setCartItems(getGuestCart());
      return;
    }

    // Logged-in flow
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_BASE_URL}/UserManagement/GetCartByUserId/${user.userId}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      const data = await response.json();
      setCartItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('🛒 Failed to fetch cart items:', err);
      setCartItems([]);
    }
  };

  const calculateSubtotal = () => {
    return cartItems
      .reduce((total, item) => total + Number(item.price || 0), 0)
      .toFixed(2);
  };

  const deleteCartItem = async (id) => {
    // Guest delete
    if (!user) {
      deleteGuestCartItem(id);
      setCartItems(getGuestCart());
      return;
    }

    // Logged-in delete (try DELETE, fallback POST)
    try {
      const token = localStorage.getItem('token');

      let response = await fetch(`${API_BASE_URL}/UserManagement/DeleteCartItem/${id}`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (!response.ok) {
        response = await fetch(`${API_BASE_URL}/UserManagement/DeleteCartItem/${id}`, {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
      }

      if (!response.ok) throw new Error('Failed to delete cart item');

      // refresh
      await handleOpenCart();
      window.dispatchEvent(new Event('ccc_cart_updated'));
    } catch (err) {
      console.error('❌ Failed to delete cart item:', err);
    }
  };

  return (
    <>
      <header className="rbt-header rbt-header-10">
        <div className="rbt-sticky-placeholder"></div>
        <div className="rbt-header-wrapper header-space-betwween header-sticky">
          <div className="container-fluid">
            <div className="mainbar-row rbt-navigation-center align-items-center">
              {/* Logo Section */}
              <div className="header-left rbt-header-content">
                <div className="header-info">
                  <div className="logo">
                    <Link to="/">
                      <img
                        src={headerData?.logoUrl || '/assets/images/capital_clothes_logo-275x65.png'}
                        alt="Capital Logo"
                      />
                    </Link>
                  </div>
                </div>
              </div>

              {/* Navigation */}
              <div className="rbt-main-navigation d-none d-xl-block">
                <nav className="mainmenu-nav">
                  <ul className="mainmenu">
                    {headerData?.headerMenus?.map((menu, menuIndex) => (
                      <li
                        className="with-megamenu has-menu-child-item position-static"
                        key={menuIndex}
                      >
                        <a href={menu.url || '#'}>
                          {menu.title} <i className="feather-chevron-down"></i>
                        </a>

                        <div
                          className={`rbt-megamenu ${
                            (menu.shortTitles?.length || 0) > 2 ? 'grid-item-4' : 'grid-item-2'
                          }`}
                        >
                          <div className="wrapper">
                            <div className="row row--15">
                              {menu.shortTitles?.map((shortTitle, shortIndex) => (
                                <div
                                  className={`col-lg-12 col-xl-${
                                    12 / (menu.shortTitles?.length || 1)
                                  } col-xxl-${12 / (menu.shortTitles?.length || 1)} single-mega-item`}
                                  key={shortIndex}
                                >
                                  <h3 className="rbt-short-title">{shortTitle.title}</h3>
                                  <ul className="mega-menu-item">
                                    {shortTitle.submenus?.map((submenu, subIndex) => (
                                      <li key={subIndex}>
                                        <a href={submenu.url || '#'}>{submenu.title}</a>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </nav>
              </div>

              <div className="header-right">
                <ul className="quick-access">
                  <li className="access-icon">
                    <a
                      className="search-trigger-active rbt-round-btn"
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        handleOpenSearch();
                      }}
                      aria-label="Search"
                    >
                      <i className="feather-search"></i>
                    </a>
                  </li>

                  <li className="access-icon rbt-mini-cart">
                    <a
                      className="rbt-cart-sidenav-activation rbt-round-btn"
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        handleOpenCart();
                      }}
                    >
                      <i className="feather-shopping-bag"></i>
                      <span className="rbt-cart-count">{cartItems?.length || 0}</span>
                    </a>
                  </li>

                  <li
                    className="access-icon rbt-user-wrapper position-relative"
                    onMouseEnter={() => {
                      if (window.innerWidth >= 1200) {
                        document
                          .querySelector('.rbt-user-menu-list-wrapper')
                          ?.classList.add('show');
                      }
                    }}
                    onMouseLeave={() => {
                      if (window.innerWidth >= 1200) {
                        document
                          .querySelector('.rbt-user-menu-list-wrapper')
                          ?.classList.remove('show');
                      }
                    }}
                  >
                    <a
                      className="rbt-round-btn"
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        if (!user) window.location.href = '/login';
                      }}
                    >
                      <i className="feather-user"></i>
                    </a>

                    <div className="rbt-user-menu-list-wrapper">
                      <div className="inner">
                        <div className="rbt-admin-profile">
                          <div className="admin-thumbnail">
                            <img src="/assets/images/team/avatar.jpg" alt="User" />
                          </div>
                          <div className="admin-info">
                            <span className="name">{user ? `Hi, ${user.username}` : 'Hi, Guest'}</span>
                          </div>
                        </div>

                        {/* Guest menu items */}
                        {!user && (
                          <>
                            <ul className="user-list-wrapper">
                              <li>
                                <a href="/login">
                                  <i className="feather-log-in"></i>
                                  <span>Login</span>
                                </a>
                              </li>

                              <li>
                                <a href="/cart">
                                  <i className="feather-shopping-bag"></i>
                                  <span>Shopping Bag</span>
                                </a>
                              </li>

                              <li>
                                <a href="/wishlist">
                                  <i className="feather-heart"></i>
                                  <span>Wishlist</span>
                                </a>
                              </li>
                            </ul>

                            <hr className="mt--10 mb--10" />

                            <ul className="user-list-wrapper">
                              <li>
                                <a href="mailto:support@capitalclothiers.com">
                                  <i className="feather-help-circle"></i>
                                  <span>Need Help?</span>
                                </a>
                              </li>
                            </ul>
                          </>
                        )}

                        {/* Logged in menu items */}
                        {user && (
                          <>
                            <ul className="user-list-wrapper">
                              <li>
                                <Link to={`/ClientDetails/${user?.userId || ''}`}>
                                  <i className="feather-home"></i> <span>My Dashboard</span>
                                </Link>
                              </li>
                              <li>
                                <a href="/Cart">
                                  <i className="feather-shopping-bag"></i> <span>Shopping Bag</span>
                                </a>
                              </li>
                              <li>
                                <a href="/Wishlist">
                                  <i className="feather-heart"></i> <span>Wishlist</span>
                                </a>
                              </li>
                              <li>
                                <a href="/past-orders">
                                  <i className="feather-clock"></i> <span>Order History</span>
                                </a>
                              </li>
                            </ul>

                            <hr className="mt--10 mb--10" />

                            <ul className="user-list-wrapper">
                              <li>
                                <a href="instructor-settings.html">
                                  <i className="feather-settings"></i>
                                  <span>Settings</span>
                                </a>
                              </li>
                              <li>
                                <a
                                  href="#"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    localStorage.removeItem('user');
                                    localStorage.removeItem('token');
                                    localStorage.removeItem('username');
                                    setUser(null);
                                    window.dispatchEvent(new Event('ccc_user_updated'));
                                    window.location.href = '/';
                                  }}
                                >
                                  <i className="feather-log-out"></i>
                                  <span>Logout</span>
                                </a>
                              </li>
                            </ul>
                          </>
                        )}
                      </div>
                    </div>
                  </li>
                </ul>

                {/* Mobile Menu Toggle */}
                <div className="mobile-menu-bar d-block d-xl-none">
                  <div className="hamberger">
                    <button
                      className="hamberger-button rbt-round-btn"
                      onClick={() => setMobileMenuOpen(true)}
                    >
                      <i className="feather-menu"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Side Nav (template) */}
        <div className="rbt-offcanvas-side-menu rbt-category-sidemenu">
          <div className="inner-wrapper">
            <div className="inner-top">
              <div className="inner-title">
                <h4 className="title">Course Category</h4>
              </div>
              <div className="rbt-btn-close">
                <button className="rbt-close-offcanvas rbt-round-btn">
                  <i className="feather-x"></i>
                </button>
              </div>
            </div>

            <nav className="side-nav w-100">
              <ul className="rbt-vertical-nav-list-wrapper vertical-nav-menu">
                <li className="vertical-nav-item">
                  <a href="#">Course School</a>
                </li>
                <li className="vertical-nav-item">
                  <a href="#">Online School</a>
                </li>
                <li className="vertical-nav-item">
                  <a href="#">kindergarten</a>
                </li>
                <li className="vertical-nav-item">
                  <a href="#">Classic LMS</a>
                </li>
              </ul>

              <div className="read-more-btn">
                <div className="rbt-btn-wrapper mt--20">
                  <a
                    className="rbt-btn btn-border-gradient radius-round btn-sm hover-transform-none w-100 justify-content-center text-center"
                    href="#"
                  >
                    <span>Learn More</span>
                  </a>
                </div>
              </div>
            </nav>

            <div className="rbt-offcanvas-footer"></div>
          </div>
        </div>

        <a className="rbt-close_side_menu" href="javascript:void(0);"></a>
      </header>

      {/* Mobile Menu Section */}
      <div className={`popup-mobile-menu ${mobileMenuOpen ? 'active' : ''}`}>
        <div className="inner-wrapper">
          <div className="inner-top">
            <div className="content">
              <div className="logo">
                <a href="index.html">
                  <img
                    src="/assets/images/capital_clothes_logo-275x65.png"
                    alt="Education Logo Images"
                  />
                </a>
              </div>
              <div className="rbt-btn-close">
                <button
                  className="close-button rbt-round-btn"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <i className="feather-x"></i>
                </button>
              </div>
            </div>

            <p className="description">
              Lorem ipsum dolor sit amet consectetur, adipisicing elit. Omnis cum dignissimos at?
              Tempora, libero aut!
            </p>

            <ul className="navbar-top-left rbt-information-list justify-content-start">
              <li>
                <a href="mailto:hello@example.com">
                  <i className="feather-mail"></i>example@gmail.com
                </a>
              </li>
              <li>
                <a href="#">
                  <i className="feather-phone"></i>(302) 555-0107
                </a>
              </li>
            </ul>
          </div>

          <nav className="mainmenu-nav">
            <ul className="mainmenu">
              {headerData?.headerMenus?.map((menu, menuIndex) => {
                const isOpen = openMobileMenus.includes(menuIndex);
                return (
                  <li
                    key={menuIndex}
                    className={`with-megamenu has-menu-child-item ${isOpen ? 'open' : ''}`}
                  >
                    <a
                      href={menu.url || '#'}
                      onClick={(e) => {
                        e.preventDefault();
                        toggleMobileMenu(menuIndex);
                      }}
                      className={isOpen ? 'open' : ''}
                    >
                      {menu.title}
                    </a>

                    <div
                      className={`rbt-megamenu ${
                        (menu.shortTitles?.length || 0) > 2 ? 'grid-item-4' : 'grid-item-2'
                      }`}
                      style={{ display: isOpen ? 'block' : 'none' }}
                    >
                      <div className="wrapper">
                        <div className="row row--15">
                          {menu.shortTitles?.map((shortTitle, shortIndex) => (
                            <div
                              key={shortIndex}
                              className={`col-lg-12 col-xl-${
                                12 / (menu.shortTitles?.length || 1)
                              } col-xxl-${12 / (menu.shortTitles?.length || 1)} single-mega-item`}
                            >
                              <h3 className="rbt-short-title">{shortTitle.title}</h3>
                              <ul className="mega-menu-item">
                                {shortTitle.submenus?.map((submenu, subIndex) => (
                                  <li key={subIndex}>
                                    <a href={submenu.url || '#'}>{submenu.title}</a>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </nav>

          <div className="mobile-menu-bottom">
            <div className="rbt-btn-wrapper mb--20">
              <a
                className="rbt-btn btn-border-gradient radius-round btn-sm hover-transform-none w-100 justify-content-center text-center"
                href="#"
              >
                <span>Shop Now</span>
              </a>
            </div>

            <div className="social-share-wrapper">
              <span className="rbt-short-title d-block">Find With Us</span>
              <ul className="social-icon social-default transparent-with-border justify-content-start mt--20">
                <li>
                  <a href="https://www.facebook.com/">
                    <i className="feather-facebook"></i>
                  </a>
                </li>
                <li>
                  <a href="https://www.twitter.com">
                    <i className="feather-twitter"></i>
                  </a>
                </li>
                <li>
                  <a href="https://www.instagram.com/">
                    <i className="feather-twitter"></i>
                  </a>
                </li>
                <li>
                  <a href="https://www.linkdin.com/">
                    <i className="feather-linkedin"></i>
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Cart Side Menu */}
      {showCart && (
        <div className={`rbt-cart-side-menu${showCart ? ' side-menu-active' : ''}`}>
          <div className="inner-wrapper">
            <div className="inner-top">
              <div className="content">
                <div className="title">
                  <h4 className="title mb--0">Your shopping bag</h4>
                </div>
                <div className="rbt-btn-close">
                  <button
                    className="minicart-close-button rbt-round-btn"
                    onClick={() => setShowCart(false)}
                  >
                    <i className="feather-x"></i>
                  </button>
                </div>
              </div>
            </div>

            <>
              <nav className="side-nav w-100">
                <ul className="rbt-minicart-wrapper">
                  {cartItems?.map((item, index) => {
                    const key = user ? item.cartId ?? index : item.guestCartId ?? index;
                    const img = item.fabricImageUrl || item.imageUrl || '/assets/images/product/1.jpg';
                    const price = Number(item.price || 0);

                    return (
                      <li className="minicart-item" key={key}>
                        <div className="thumbnail">
                          <a href="#">
                            <img src={img} alt="Product" />
                          </a>
                        </div>
                        <div className="product-content">
                          <h6 className="title">
                            <a href="#">{item.productName || 'Product'}</a>
                          </h6>
                          <span className="quantity">
                            1 * <span className="price">${price.toFixed(2)}</span>
                          </span>
                          {!user && (
                            <div style={{ fontSize: '12px', color: '#777', marginTop: '4px' }}>
                              Saved for this session
                            </div>
                          )}
                        </div>
                        <div className="close-btn">
                          <button
                            className="rbt-round-btn"
                            onClick={() => deleteCartItem(user ? item.cartId : item.guestCartId)}
                          >
                            <i className="feather-x"></i>
                          </button>
                        </div>
                      </li>
                    );
                  })}

                  {(!cartItems || cartItems.length === 0) && (
                    <li style={{ padding: '18px', textAlign: 'center', color: '#777' }}>
                      Your cart is empty.
                    </li>
                  )}
                </ul>
              </nav>

              <div className="rbt-minicart-footer">
                <hr className="mb--0" />
                <div className="rbt-cart-subttotal">
                  <p className="subtotal">
                    <strong>Subtotal:</strong>
                  </p>
                  <p className="price">${calculateSubtotal()}</p>
                </div>
                <hr className="mb--0" />

                <div className="rbt-minicart-bottom mt--20">
                  <div className="view-cart-btn">
                    <Link className="rbt-btn btn-border icon-hover w-100 text-center" to="/cart">
                      <span className="btn-text">View Cart</span>
                      <span className="btn-icon">
                        <i className="feather-arrow-right"></i>
                      </span>
                    </Link>
                  </div>

                  <div className="checkout-btn mt--20">
                    {user ? (
                      <Link
                        className="rbt-btn btn-gradient icon-hover w-100 text-center"
                        to="/checkout"
                      >
                        <span className="btn-text">Checkout</span>
                        <span className="btn-icon">
                          <i className="feather-arrow-right"></i>
                        </span>
                      </Link>
                    ) : (
                      <Link className="rbt-btn btn-gradient icon-hover w-100 text-center" to="/login">
                        <span className="btn-text">Login to Checkout</span>
                        <span className="btn-icon">
                          <i className="feather-arrow-right"></i>
                        </span>
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </>
          </div>
        </div>
      )}

      {/* ✅ Search Side Menu (Cart-style) */}
      {showSearchPanel && (
        <div className="rbt-cart-side-menu side-menu-active">
          <div className="inner-wrapper">
            <div className="inner-top">
              <div className="content">
                <div className="title">
                  <h4 className="title mb--0">Search</h4>
                </div>
                <div className="rbt-btn-close">
                  <button
                    className="minicart-close-button rbt-round-btn"
                    onClick={handleCloseSearch}
                  >
                    <i className="feather-x"></i>
                  </button>
                </div>
              </div>
            </div>

            <div style={{ padding: 18 }}>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  runSearch(searchQuery);
                }}
              >
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search products, pages, or Custom Fabric ID (e.g., CF-1023)…"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setActiveIndex(-1);
                      setSearchError('');
                    }}
                    onKeyDown={(e) => {
                      const items = combinedSuggestions;

                      if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        setActiveIndex((i) => Math.min(i + 1, items.length - 1));
                      }

                      if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        setActiveIndex((i) => Math.max(i - 1, -1));
                      }

                      if (e.key === 'Enter' && activeIndex >= 0) {
                        e.preventDefault();
                        chooseSuggestion(items[activeIndex]);
                      }
                    }}
                    style={{ height: 44, flex: 1 }}
                    autoFocus
                  />

                  <button
                    className="rbt-btn btn-gradient btn-md"
                    type="submit"
                    style={{ height: 44, paddingTop: 0, paddingBottom: 0 }}
                  >
                    Search
                  </button>
                </div>
              </form>

              <div style={{ marginTop: 14 }}>
                {searchLoading && <div style={{ color: '#666' }}>Searching…</div>}
                {!searchLoading && searchError && (
                  <div style={{ color: '#b00020' }}>{searchError}</div>
                )}

                {!searchLoading && !searchError && searchQuery.trim() && (
                  <>
                    {combinedSuggestions.length ? (
                      <div style={{ marginTop: 10 }}>
                        {combinedSuggestions.map((item, idx) => (
                          <div
                            key={`${item.type}-${item.title}-${idx}`}
                            style={{
                              display: 'flex',
                              gap: 10,
                              alignItems: 'center',
                              cursor: 'pointer',
                              padding: '10px 0',
                              background: idx === activeIndex ? '#f6f6f6' : 'transparent',
                              borderRadius: 8
                            }}
                            onMouseEnter={() => setActiveIndex(idx)}
                            onClick={() => chooseSuggestion(item)}
                          >
                            <div
                              style={{
                                fontSize: 12,
                                padding: '4px 8px',
                                borderRadius: 999,
                                border: '1px solid #e5e5e5',
                                color: '#333',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {item.type}
                            </div>

                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 600 }}>{item.title}</div>
                              {!!item.subtitle && (
                                <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
                                  {item.subtitle}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ color: '#666', marginTop: 10 }}>No results found.</div>
                    )}

                    <div style={{ marginTop: 12, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="rbt-btn btn-border btn-sm"
                        onClick={() => runSearch(searchQuery)}
                      >
                        View all results
                      </button>

                      <div style={{ fontSize: 12, color: '#777', paddingTop: 8 }}>
                        Tip: search by <strong>Custom Fabric ID</strong> (example:{' '}
                        <strong>CF-1023</strong>)
                      </div>
                    </div>
                  </>
                )}

                {!searchQuery.trim() && (
                  <div style={{ color: '#777', marginTop: 10 }}>
                    Tip: search by <strong>Custom Fabric ID</strong> (example:{' '}
                    <strong>CF-1023</strong>)
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;

